import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
} from 'firebase/auth'
import type { User } from 'firebase/auth'
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore'
import { auth, db, googleProvider } from './firebase'

/**
 * Whether the signed-in user still has to answer the onboarding survey.
 *
 * Mirrors `AppState.checkAccountStatus` in the Swift app: the presence of a
 * `role` field on `users/{uid}` is what marks onboarding complete. Keeping the
 * same signal means someone who onboarded on the Mac is not asked again here.
 */
export type OnboardingState = 'unknown' | 'required' | 'completed'

/** A live act-as grant, read from the ID token rather than from app state. */
export interface ActingGrant {
  /** The account being acted as. */
  ownerUid: string
  /** Epoch ms the grant dies at, or 0 for one that lasts until released. */
  until: number
}

interface AuthContextValue {
  user: User | null
  /**
   * Who the app is acting as.
   *
   * Almost always the signed-in user. While an admin holds an act-as grant it
   * is the account they're building — and because the rules resolve `uid()`
   * through the same claim, every query and every write lines up without a
   * second code path existing to make it so.
   *
   * Use this for data. Use `user` only for things that are genuinely about the
   * signed-in person: sign-out, password changes, email verification.
   */
  uid: string | null
  /** Non-null while a grant is live. Drives the banner. */
  acting: ActingGrant | null
  /** Whose account it is, resolved from the record rather than from storage. */
  actingName: string | null
  /** True until the first auth callback lands — distinct from "signed out". */
  initialising: boolean
  onboarding: OnboardingState
  needsEmailVerification: boolean
  refreshAccountStatus: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, displayName: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  resendVerification: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  reloadUser: () => Promise<boolean>
  signOut: () => Promise<void>
  refreshClaims: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Creates `users/{uid}` if it isn't there yet.
 *
 * Crew lists resolve names and avatars out of this collection, and invites
 * look an email up here before sending, so an account with no user document is
 * invisible to everyone else on their tours.
 */
async function ensureUserDocument(user: User): Promise<void> {
  const ref = doc(db, 'users', user.uid)
  const snapshot = await getDoc(ref)

  const profile = {
    email: user.email?.toLowerCase() ?? null,
    displayName: user.displayName ?? null,
    photoURL: user.photoURL ?? null,
  }

  if (!snapshot.exists()) {
    await setDoc(ref, { ...profile, createdAt: serverTimestamp() })
    return
  }

  // Keep the denormalised copy fresh without clobbering `role` or the
  // tutorial flags the native apps own.
  await setDoc(ref, profile, { merge: true })
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [initialising, setInitialising] = useState(true)
  const [onboarding, setOnboarding] = useState<OnboardingState>('unknown')
  const [acting, setActing] = useState<ActingGrant | null>(null)
  const [actingName, setActingName] = useState<string | null>(null)

  useEffect(() => {
    return onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser)

      if (!nextUser) {
        setOnboarding('unknown')
        setActing(null)
        setInitialising(false)
        return
      }

      // Read the grant off the token, not off app state: the token is what the
      // rules will see, so anything else can disagree with them.
      //
      // Always forced. Firebase caches an ID token for up to an hour, so a
      // page load can read a token from before the claim was set — the grant
      // exists on the server, the rules never see it, and the app comes up as
      // yourself instead of the account you asked for.
      //
      // The previous version only forced this when a sessionStorage flag was
      // present, which a new tab does not have. Opening the app in a second
      // tab therefore dropped you out of the account you were building in,
      // with nothing on screen explaining why. One network round trip per load
      // is worth not having that happen.
      try {
        const token = await nextUser.getIdTokenResult(true)
        const actAs = token.claims.actAs as string | undefined
        const until = Number(token.claims.actAsUntil ?? 0)
        setActing(
          actAs && (until === 0 || until > Date.now()) ? { ownerUid: actAs, until } : null,
        )
      } catch {
        setActing(null)
      }

      try {
        await ensureUserDocument(nextUser)
        const snapshot = await getDoc(doc(db, 'users', nextUser.uid))
        setOnboarding(snapshot.data()?.role ? 'completed' : 'required')
      } catch {
        // Offline on load. Assume completed rather than trapping a returning
        // user in onboarding they already finished on another device.
        setOnboarding('completed')
      }

      setInitialising(false)
    })
  }, [])

  const value = useMemo<AuthContextValue>(() => {
    const refreshAccountStatus = async () => {
      if (!auth.currentUser) return
      const snapshot = await getDoc(doc(db, 'users', auth.currentUser.uid))
      setOnboarding(snapshot.data()?.role ? 'completed' : 'required')
    }

    return {
      user,
      uid: acting?.ownerUid ?? user?.uid ?? null,
      acting,
      actingName,
      initialising,
      onboarding,
      // Google accounts arrive verified; only the email/password path gates.
      needsEmailVerification: !!user && !user.emailVerified && !isGoogleAccount(user),
      refreshAccountStatus,

      signIn: async (email, password) => {
        await signInWithEmailAndPassword(auth, email.trim(), password)
      },

      signUp: async (email, password, displayName) => {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password)
        const trimmedName = displayName.trim()
        if (trimmedName) await updateProfile(credential.user, { displayName: trimmedName })
        await ensureUserDocument(credential.user)
        await sendEmailVerification(credential.user)
      },

      signInWithGoogle: async () => {
        const credential = await signInWithPopup(auth, googleProvider)
        await ensureUserDocument(credential.user)
      },

      resendVerification: async () => {
        if (auth.currentUser) await sendEmailVerification(auth.currentUser)
      },

      resetPassword: async (email) => {
        await sendPasswordResetEmail(auth, email.trim())
      },

      /** Re-reads the account after the user says they've clicked the link. */
      reloadUser: async () => {
        if (!auth.currentUser) return false
        await auth.currentUser.reload()
        setUser({ ...auth.currentUser } as User)
        return auth.currentUser.emailVerified
      },

      signOut: async () => {
        await firebaseSignOut(auth)
      },

      /**
       * Re-reads the token's claims.
       *
       * Called after granting or releasing a grant: `setCustomUserClaims`
       * changes the token on the server, and the client keeps using its cached
       * one until it is forced to fetch a new one.
       */
      refreshClaims: async () => {
        if (!auth.currentUser) return
        const token = await auth.currentUser.getIdTokenResult(true)
        const actAs = token.claims.actAs as string | undefined
        const until = Number(token.claims.actAsUntil ?? 0)
        setActing(
          actAs && (until === 0 || until > Date.now()) ? { ownerUid: actAs, until } : null,
        )
      },
    }
  }, [user, initialising, onboarding, acting, actingName])

  useEffect(() => {
    if (!acting) {
      setActingName(null)
      return
    }
    let alive = true
    getDoc(doc(db, 'users', acting.ownerUid))
      .then((snapshot) => {
        if (!alive) return
        const data = snapshot.data()
        setActingName((data?.displayName as string) || (data?.email as string) || null)
      })
      .catch(() => {
        if (alive) setActingName(null)
      })
    return () => {
      alive = false
    }
  }, [acting])

  useEffect(() => {
    if (!acting || acting.until === 0) return
    const remaining = acting.until - Date.now()
    if (remaining <= 0) {
      setActing(null)
      return
    }
    const timer = setTimeout(() => setActing(null), remaining)
    return () => clearTimeout(timer)
  }, [acting])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function isGoogleAccount(user: User): boolean {
  return user.providerData.some((entry) => entry.providerId === 'google.com')
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
